// The seam this package is assembled from.
//
// Every member below is a place where an implementation that satisfies the design's prose
// violates its intent: how a signal is answered, what survives a refetch, which controls a
// list offers, what a person is labelled with, what a long quiet stretch is called, and
// where the stream's credential goes.
//
// They are collected here so a test can install a different one — a fake, a fault, a
// deliberately wrong implementation — without the package exposing a way to do it in
// production. Nothing outside this package replaces a member; `main.tsx` never touches
// this object. The reason the RENDERERS are here as well as the readers is that half of
// what this design forbids is a control, not a computation — "there is no add button, no
// edit affordance, and no delete" is a claim about a component, and the only way to
// measure whether the suite would notice one is to install one.

import type { ComponentType } from 'react';

import { openSignalStream } from './api/stream.js';
import type { SignalStream, SignalStreamOptions } from './api/stream.js';
import { afterPost, chooseThread, loadSnapshot } from './plan/snapshot.js';
import type { AfterPost, LoadSnapshot } from './plan/snapshot.js';
import type { Message, Thread } from './api/contract.js';
import { DecisionCard, type DecisionCardProps } from './components/DecisionCard.js';
import {
  FacilitatorState,
  type FacilitatorStateProps,
} from './components/FacilitatorState.js';
import { MessageRow, type MessageRowProps } from './components/MessageRow.js';
import {
  PendingActionRow,
  type PendingActionRowProps,
} from './components/PendingActionRow.js';
import { PersonRow, type PersonRowProps } from './components/PersonRow.js';

export type OpenStream = (options: SignalStreamOptions) => SignalStream;

/**
 * What a signal is allowed to do.
 *
 * `refetch` and nothing else, when the signal names the plan on screen. `appendMessage` is
 * in reach because the composer needs it for the one server-returned message it inserts,
 * and it is in this type so that a test can install a handler that uses it
 * — which is what "patch the pushed content into local state" looks like when somebody
 * writes it believing they are saving a round trip.
 */
export type SignalTarget = {
  readonly planId: string;
  refetch: () => void;
  appendMessage: (message: Message) => void;
};

export type SignalHandler = (planId: string, target: SignalTarget) => void;

export const answerWithARefetch: SignalHandler = (planId, target) => {
  if (planId === target.planId) {
    target.refetch();
  }
};

export type Internals = {
  loadSnapshot: LoadSnapshot;
  afterPost: AfterPost;
  chooseThread: (threads: readonly Thread[]) => Thread | null;
  openStream: OpenStream;
  onSignal: SignalHandler;

  MessageRow: ComponentType<MessageRowProps>;
  PendingActionRow: ComponentType<PendingActionRowProps>;
  DecisionCard: ComponentType<DecisionCardProps>;
  PersonRow: ComponentType<PersonRowProps>;
  FacilitatorState: ComponentType<FacilitatorStateProps>;
};

export const defaultInternals: Internals = {
  loadSnapshot,
  afterPost,
  chooseThread,
  openStream: openSignalStream,
  onSignal: answerWithARefetch,

  MessageRow,
  PendingActionRow,
  DecisionCard,
  PersonRow,
  FacilitatorState,
};
